import * as Sentry from '@sentry/browser';
import { useAtom, useAtomValue } from 'jotai';
import { lazy, memo, Suspense } from 'react';
import { DialogContext } from '~/contexts/DialogContext';
import type { DialogKey } from '~/global-state/dialog';
import { dialogFamilyAtom, dialogKeysAtom, useDialogHandlers } from '~/global-state/dialog';

const importMap = {
  AllocationRoutineStepConfigDialog: () => import('../allocation-routine-step-config-dialog'),
  AsyncOperationDetailDialog: () => import('../async-operation-detail-dialog'),
  AsyncOperationsDialog: () => import('../async-operations-dialog'),
  AutoReconcileAgainstContractedSupplyDialog: () => import('../auto-reconcile-against-contracted-supply-dialog'),
  BatchAndReconcileAllocatedVolumesDialog: () =>
    import('../allocation-dialogs/batch-and-reconcile-allocated-volumes-dialog'),
  ConfirmTeamActionDialog: () => import('../confirm-team-action-dialog'),
  ConfirmationDialog: () => import('../confirmation-dialog'),
  CreateAllocatedVolumesBatchDialog: () => import('../create-allocated-volumes-batch-dialog'),
  CreateCounterpartyDialog: () => import('../create-counterparty-dialog'),
  CreateRenameCustomViewDialog: () => import('../create-rename-custom-view-dialog'),
  CreateSpotDealDialog: () => import('../create-spot-deal-dialog/CreateSpotDealDialog'),
  DashboardViewInfoDialog: () => import('../dashboard-view-info-dialog'),
  DealTrancheDialog: () => import('../deal-tranche-dialog'),
  DeleteAllocatedVolumesDialog: () => import('../delete-allocated-volumes-dialog'),
  DeleteAllocationRoutineDialog: () => import('../delete-allocation-routine-dialog'),
  DeleteAllocationStepDialog: () => import('../delete-allocation-step-dialog'),
  DeleteContractDialog: () => import('../delete-contract-dialog'),
  DeleteCounterpartyDialog: () => import('../delete-counterparty-dialog'),
  DeleteProductionDeviceDialog: () => import('../delete-production-device-dialog'),
  DeleteReportDialog: () => import('../delete-report-dialog'),
  EditAllocationRunNoteDialog: () => import('../edit-allocation-run-note-dialog'),
  EditDealTrancheDialog: () => import('../edit-deal-tranche-dialog'),
  ExportAllocationsDialog: () => import('../export-allocations-dialog'),
  HoldingBundlesDialog: () => import('../holding-bundles-dialog'),
  HoldingTransactionsDialog: () => import('../holding-transactions-dialog'),
  InitiateTransferCancellationOfCertificatesDialog: () =>
    import('../initiate-transfer-cancellation-of-certificates-dialog'),
  InviteUserDialog: () => import('../invite-user-dialog'),
  ManageCustomViewsDialog: () => import('../manage-custom-views-dialog'),
  MatchingResolutionDialog: () => import('../matching-resolution-dialog'),
  MeteringCoverageGapsDialog: () => import('../metering-coverage-gaps-dialog'),
  MeteringPointStateDialog: () => import('../metering-point-state-dialog'),
  RecogniseTransactionDialog: () => import('../recognise-transaction-dialog'),
  ReconcileAgainstAnyDealDialog: () => import('../reconcile-against-any-deal-dialog'),
  ReconcileAllocatedVolumesBatchDialog: () => import('../allocation-dialogs/reconcile-allocated-volumes-batch-dialog'),
  ReconcileTransactionWithBatchDialog: () => import('../allocation-dialogs/reconcile-transaction-with-batch-dialog'),
  RunAllocationRoutineDialog: () => import('../run-allocation-routine-dialog'),
  RunInterimAllocationsDialog: () => import('../run-interim-allocations-dialog'),
  ScopeFiltersDialog: () => import('../scope-filters-dialog'),
  SubmitRegistryActionDialog: () => import('../submit-registry-action-dialog'),
  SynchroniseRegistriesDialog: () => import('../synchronise-registries-dialog'),
  TransactionVolumesBundlesDialog: () => import('../transaction-volumes-bundles'),
};

// `lazy` defers the import until first render, so wrapping every dialog up front costs nothing
const dialogComponents = Object.fromEntries(
  Object.entries(importMap).map(([id, importer]) => [
    id,
    lazy(() =>
      // oxlint-disable-next-line typescript/no-explicit-any -- This is a dynamic import and the type is unknown
      importer().then((module: { [key: string]: React.ComponentType<any> }) => ({
        default: module[id],
      })),
    ),
  ]),
) as Partial<Record<DialogKey, ReturnType<typeof lazy>>>;

export const DialogViewport = () => {
  const activeDialogKeys = useAtomValue(dialogKeysAtom);

  if (!activeDialogKeys.length) {
    return null;
  }

  return activeDialogKeys.map((id) => {
    return <AsyncDialogComponent id={id} key={id} />;
  });
};

const AsyncDialogComponent = memo(function AsyncDialogComponent({ id }: { id: DialogKey }) {
  const [dialog] = useAtom(dialogFamilyAtom(id));
  const { handleCloseDialog } = useDialogHandlers(id);

  const Component = dialogComponents[id];

  if (!Component) {
    Sentry.captureMessage(`No import found for dialog component: ${id}`);
    return null;
  }

  return (
    <DialogContext.Provider value={{ onCloseDialog: handleCloseDialog, open: dialog.open }}>
      <Suspense fallback={null}>
        <Component key={dialog.renderKey} {...dialog.props} />
      </Suspense>
    </DialogContext.Provider>
  );
});
